import {
  InlineDatumReactComponentProps,
  useAnalytics,
  useInlineDataEvents,
} from '@stripe-internal/inline-data';
import {ChatButtonOptions} from '@stripe-internal/conversations-ui';
import {defineMessages, useIntl} from 'react-intl';
import {useState, useRef, useEffect} from 'react';
import {v4 as uuid} from 'uuid';
import {view} from '@sail/ui';
import SendUsAnEmailSection, {
  EmailForm,
  FollowUpEmailClientErrors,
} from '../helpers/SendUsAnEmailSection';

export type ValueProps = {
  show_contact_support_options?: boolean;
  email_form?: EmailForm;
  error_code?: FollowUpEmailClientErrors;
  is_email_form_submitted?: boolean;
  show_cc_emails_field?: boolean;
  existing_cc_emails?: string[];
};

const messages = defineMessages({
  followUpOnCaseOption: {
    id: 'mse_inline_data.merchant_follow_up_case.follow_up_on_case',
    description: 'Option for following up on the existing case',
    defaultMessage: 'Follow up about this case',
  },
  differentCaseOption: {
    id: 'mse_inline_data.merchant_follow_up_case.check_status',
    description: 'Option for checking the status of a different case',
    defaultMessage: 'Check status of a different case',
  },
  newIssueOption: {
    id: 'mse_inline_data.merchant_follow_up_case.new_issue',
    description: 'Option for indicating this is a new issue',
    defaultMessage: 'I have a new issue',
  },
  contactSupportFollowUpOnCaseOption: {
    id: 'mse_inline_data.merchant_follow_up_case.yes_follow_up_on_case',
    description: 'Option for following up on the existing case',
    defaultMessage: 'Yes, follow up on this case',
  },
  contactSupportDifferentCaseOption: {
    id: 'mse_inline_data.merchant_follow_up_case.no_different_case',
    description: 'Option for indicating this is a different case',
    defaultMessage: 'No, I have a different case',
  },
  contactSupportNewIssueOption: {
    id: 'mse_inline_data.merchant_follow_up_case.no_new_issue',
    description: 'Option for indicating this is a new issue',
    defaultMessage: 'No, this is a new issue',
  },
});

type OptionIds = 'channel_email' | 'different_case' | 'new_issue';

export const MerchantFollowUpCaseOptions = (
  props: InlineDatumReactComponentProps,
) => {
  const {datum, onDone, action, isDone, actionError} = props;
  const value = datum.value as ValueProps;
  const events = useInlineDataEvents();
  const intl = useIntl();
  const [selectedOption, setSelectedOption] = useState<OptionIds | undefined>(
    undefined,
  );

  const {
    show_contact_support_options: showContactSupportOptions,
    email_form: emailForm,
    error_code: errorCode,
    is_email_form_submitted: isEmailFormSubmitted,
    show_cc_emails_field: showCcEmailsField,
    existing_cc_emails: existingCcEmails,
  } = value;

  useEffect(() => {
    if (selectedOption !== 'channel_email' || !isEmailFormSubmitted || isDone) {
      return;
    }

    onDone({
      type: 'success',
      extra: {
        outcome: selectedOption,
        value,
      },
    });
  }, [isDone, isEmailFormSubmitted, onDone, selectedOption, value]);

  const nonce = useRef<string>(uuid()).current;
  const analytics = useAnalytics();

  const onConvOptionPress = (optionId: OptionIds) => {
    events.emit({name: 'inline-data-trigger-scroll-to-top'});
    setSelectedOption(optionId);
    analytics.action('uh_merchant_follow_up_case_option_selected', {
      followUpCaseOption: optionId,
    });
    if (optionId !== 'channel_email') {
      onDone({
        type: 'success',
        extra: {
          outcome: optionId,
        },
      });
    } else {
      events.emitWithResult({
        name: 'inline-data-action',
        data: {
          purpose: 'channel_selection',
          channelID: optionId,
        },
        nonce,
      });
    }
  };

  const optionMessages = showContactSupportOptions
    ? [
        messages.contactSupportFollowUpOnCaseOption,
        messages.contactSupportDifferentCaseOption,
        messages.contactSupportNewIssueOption,
      ]
    : [
        messages.followUpOnCaseOption,
        messages.differentCaseOption,
        messages.newIssueOption,
      ];

  const optionIds: OptionIds[] = [
    'channel_email',
    'different_case',
    'new_issue',
  ];

  const convOptions = optionIds.map((optionId, index) => {
    return {
      value: optionId,
      label: intl.formatMessage(optionMessages[index]),
      onPress: () => onConvOptionPress(optionId),
    };
  });

  const handleCancelForm = () => {
    analytics.action('uh_merchant_follow_up_case_cancel_form');
    setSelectedOption(undefined);
  };

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <ChatButtonOptions
        options={convOptions}
        selectedOptionId={selectedOption}
        animated
      />
      <SendUsAnEmailSection
        action={action}
        nonce={nonce}
        handleCancelForm={handleCancelForm}
        emailForm={emailForm}
        isSectionVisible={selectedOption === 'channel_email'}
        isEmailFormSubmitted={isDone || !!isEmailFormSubmitted}
        showUnauthenticatedEmailField={false}
        showCcEmailsField={showCcEmailsField}
        existingCcEmails={existingCcEmails}
        showContactSupportOptions={showContactSupportOptions}
        clientError={
          errorCode ? (errorCode as FollowUpEmailClientErrors) : undefined
        }
        serverError={actionError}
      />
    </view.div>
  );
};
